import * as THREE from "three";
import {
  sampleAuthoredTrackBank,
  TRACK_CONTROL_POINTS,
} from "../track/definition";

const WORLD_UP = new THREE.Vector3(0, 1, 0);

export type TrackFrame = {
  center: THREE.Vector3;
  position: THREE.Vector3;
  tangent: THREE.Vector3;
  right: THREE.Vector3;
  normal: THREE.Vector3;
  quaternion: THREE.Quaternion;
};

export type SuntraceTrack = {
  curve: THREE.CatmullRomCurve3;
  length: number;
  width: number;
  sample: (progress: number, lateralOffset?: number, target?: TrackFrame) => TrackFrame;
  samplePlacement: (
    progress: number,
    lateralOffset?: number,
    target?: TrackFrame,
  ) => TrackFrame;
};

const createTrackFrame = (): TrackFrame => ({
  center: new THREE.Vector3(),
  position: new THREE.Vector3(),
  tangent: new THREE.Vector3(),
  right: new THREE.Vector3(),
  normal: new THREE.Vector3(),
  quaternion: new THREE.Quaternion(),
});

export const wrapProgress = (progress: number): number =>
  ((progress % 1) + 1) % 1;

export const createSuntraceTrack = (): SuntraceTrack => {
  const points = TRACK_CONTROL_POINTS.map(
    ([x, y, z]) => new THREE.Vector3(x, y, z),
  );
  const curve = new THREE.CatmullRomCurve3(points, true, "centripetal", 0.45);
  const rotationMatrix = new THREE.Matrix4();
  const bankRotation = new THREE.Quaternion();
  const sampleFrame = (
    progress: number,
    lateralOffset: number,
    target: TrackFrame,
    banked: boolean,
  ): TrackFrame => {
    const wrappedProgress = wrapProgress(progress);
    curve.getPointAt(wrappedProgress, target.center);
    curve.getTangentAt(wrappedProgress, target.tangent).normalize();
    if (banked) {
      target.right.crossVectors(WORLD_UP, target.tangent).normalize();
      target.normal.crossVectors(target.tangent, target.right).normalize();
      bankRotation.setFromAxisAngle(
        target.tangent,
        sampleAuthoredTrackBank(wrappedProgress),
      );
      target.right.applyQuaternion(bankRotation);
      target.normal.applyQuaternion(bankRotation);
    } else {
      target.tangent.setY(0).normalize();
      target.right.crossVectors(WORLD_UP, target.tangent).normalize();
      target.normal.copy(WORLD_UP);
    }
    target.position
      .copy(target.center)
      .addScaledVector(target.right, lateralOffset)
      .addScaledVector(target.normal, 0.08);
    rotationMatrix.makeBasis(target.right, target.normal, target.tangent);
    target.quaternion.setFromRotationMatrix(rotationMatrix);
    return target;
  };

  return {
    curve,
    length: curve.getLength(),
    width: 13.4,
    sample(progress, lateralOffset = 0, target = createTrackFrame()) {
      return sampleFrame(progress, lateralOffset, target, true);
    },
    samplePlacement(progress, lateralOffset = 0, target = createTrackFrame()) {
      return sampleFrame(progress, lateralOffset, target, false);
    },
  };
};
